import { CHART_TYPES, TIMEFRAME_LABELS, indicatorByName } from './catalog'
import type { SavedDrawing } from './workbenchState'
import type { ChartTypeId, IndicatorSettings, VolumeState, WorkbenchNote } from './types'

/**
 * Turns a serialized chart environment into the human-readable "chart context"
 * text attached to AI chat messages, plus a map of diffable items so later
 * pushes send only what changed. Pure module — trivially unit-testable.
 *
 * Item keys are stable across sessions:
 *   market            — symbol · timeframe (candle style is appearance, not
 *                       strategy input — it must never read as a change)
 *   setup:<setupId>   — paired entry+exit trade markers (extendData.setupId)
 *   leg:<ctxId>       — unpaired trade markers
 *   draw:<ctxId>      — geometric drawings (ctxId stamped into extendData)
 *   ind:<name>[#k]    — indicator instances (per-base ordinal: first keeps
 *                       ind:<name>, duplicates get ind:<name>#2, #3, …)
 *   note:<id>         — on-chart notes
 * The visible window and capture time are header text, NOT items — panning
 * or zooming must never read as a chart change.
 */

export interface ChartContext {
  label: string
  text: string
  items: Record<string, string>
}

/** The chart environment the context is built from — structurally a symbol
 * plus the symbol-bound fields of a persisted slice (SymbolSliceState). */
export interface ChartContextState {
  symbol: string
  timeframe: string
  chartType: ChartTypeId
  indicators: Array<{ name: string; settings: IndicatorSettings }>
  volume: VolumeState
  notes: WorkbenchNote[]
  drawings: SavedDrawing[]
}

export interface BuildChartContextOpts {
  precision?: number | null
  /** Visible candle window (ms timestamps) at push time. */
  window?: { fromTs: number; toTs: number } | null
  /** aiPush.sentItems from the last commit → incremental mode. */
  prevSentItems?: Record<string, string> | null
  /** Injectable clock for tests. */
  now?: Date
}

const ENTRY_NAMES = ['buyEntry', 'sellEntry']
const LEG_NAMES = ['buyEntry', 'sellEntry', 'exitPoint', 'levelLine']

const NOTE_CAP = 280
const ITEM_CAP = 400
const MAX_DRAW_ITEMS = 30
const TEXT_CAP = 12000

const pad = (n: number) => String(n).padStart(2, '0')

/** UTC 'YYYY-MM-DD HH:mm' — charts are market data, local timezones would lie. */
function fmtTs(ms: number | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return 'unknown time'
  const d = new Date(ms)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

function clip(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s
}

interface Ctx {
  price: (v: number | undefined) => string
}

function ed(d: SavedDrawing): Record<string, unknown> {
  return d.extendData ?? {}
}

function reason(d: SavedDrawing): string {
  const note = ed(d).note
  return typeof note === 'string' && note.trim() ? ` — reason: "${clip(note.trim(), NOTE_CAP)}"` : ''
}

function legLine(d: SavedDrawing, ctx: Ctx): string {
  const p = d.points[0] ?? {}
  const label =
    d.name === 'buyEntry' ? 'Buy entry' : d.name === 'sellEntry' ? 'Sell entry' : d.name === 'exitPoint' ? 'Exit' : String(ed(d).label ?? 'Level')
  return `${label} @ ${ctx.price(p.value)} (${fmtTs(p.timestamp)} UTC)${reason(d)}`
}

function setupBlock(n: number, entry: SavedDrawing, exit: SavedDrawing | null, ctx: Ctx): string {
  const dir = entry.name === 'sellEntry' ? 'SHORT' : 'LONG'
  const lines = [`${n}. ${dir} setup`, `   - ${legLine(entry, ctx)}`]
  if (exit) {
    lines.push(`   - ${legLine(exit, ctx)}`)
    const ev = entry.points[0]?.value
    const xv = exit.points[0]?.value
    if (typeof ev === 'number' && typeof xv === 'number' && ev !== 0) {
      const delta = xv - ev
      const pct = (delta / ev) * 100
      const sign = delta >= 0 ? '+' : '' // negative renders its own '-'
      lines.push(`   - Entry→exit move: ${sign}${ctx.price(delta)} (${sign}${pct.toFixed(2)}%)`)
    }
  } else {
    lines.push('   - (no exit marked yet)')
  }
  return lines.join('\n')
}

function drawingLine(d: SavedDrawing, ctx: Ctx): string {
  const pts = d.points
  const pt = (i: number) => {
    const p = pts[i] ?? {}
    return `${ctx.price(p.value)} (${fmtTs(p.timestamp)} UTC)`
  }
  switch (d.name) {
    case 'horizontalStraightLine':
      return `Horizontal level @ ${ctx.price(pts[0]?.value)}${reason(d)}`
    case 'verticalStraightLine':
      return `Vertical marker @ ${fmtTs(pts[0]?.timestamp)} UTC${reason(d)}`
    case 'segment':
      return `Trend line: ${pt(0)} → ${pt(1)}${reason(d)}`
    case 'rayLine':
      return `Ray: ${pt(0)} → ${pt(1)} (extends right)${reason(d)}`
    case 'straightLine':
      return `Extended line through ${pt(0)} and ${pt(1)}${reason(d)}`
    case 'parallelStraightLine':
      return `Parallel channel: ${pt(0)} → ${pt(1)}, offset line @ ${ctx.price(pts[2]?.value)}${reason(d)}`
    case 'rectangle':
      return `Rectangle zone: ${pt(0)} → ${pt(1)}${reason(d)}`
    case 'arrow':
      return `Arrow: ${pt(0)} → ${pt(1)}${reason(d)}`
    case 'fibRetracement': {
      const levels = ed(d).levels
      const enabled = Array.isArray(levels)
        ? levels
            .filter((l): l is { value: number; enabled?: boolean } => typeof l === 'object' && l !== null && typeof (l as { value?: unknown }).value === 'number')
            .filter((l) => l.enabled !== false)
            .map((l) => l.value)
        : []
      const levelTxt = enabled.length > 0 ? ` (levels: ${enabled.join(', ')})` : ''
      return `Fib retracement: ${pt(0)} → ${pt(1)}${levelTxt}${reason(d)}`
    }
    default:
      return `${d.name}: ${pts.map((_, i) => pt(i)).join(' → ')}${reason(d)}`
  }
}

interface CollectedItems {
  items: Record<string, string>
  setupTexts: string[]
  legTexts: string[]
  indicatorTexts: string[]
  drawingTexts: string[]
  noteTexts: string[]
  droppedDrawings: number
}

function collect(state: ChartContextState, ctx: Ctx): CollectedItems {
  const items: Record<string, string> = {}
  const tf = TIMEFRAME_LABELS[state.timeframe as keyof typeof TIMEFRAME_LABELS] ?? state.timeframe
  items.market = `${state.symbol} · ${tf}`

  // Trade markers, excluding connector chrome.
  const drawable = state.drawings.filter((d) => !d.lock && ed(d).connector !== true)
  const legs = drawable.filter((d) => LEG_NAMES.includes(d.name))
  const entries = legs.filter((d) => ENTRY_NAMES.includes(d.name))
  const exits = legs.filter((d) => d.name === 'exitPoint')
  const otherLegs = legs.filter((d) => !ENTRY_NAMES.includes(d.name) && d.name !== 'exitPoint')

  // Pair by setupId; legacy markers fall back to chronological entry→next-exit.
  const claimed = new Set<SavedDrawing>()
  const setups: Array<{ key: string; entry: SavedDrawing; exit: SavedDrawing | null }> = []
  for (const entry of [...entries].sort((a, b) => (a.points[0]?.timestamp ?? 0) - (b.points[0]?.timestamp ?? 0))) {
    const sid = ed(entry).setupId
    let exit: SavedDrawing | null = null
    if (typeof sid === 'string') {
      exit = exits.find((x) => ed(x).setupId === sid && !claimed.has(x)) ?? null
    }
    if (!exit) {
      const entryTs = entry.points[0]?.timestamp ?? 0
      exit =
        exits
          .filter((x) => !claimed.has(x) && typeof ed(x).setupId !== 'string' && (x.points[0]?.timestamp ?? 0) >= entryTs)
          .sort((a, b) => (a.points[0]?.timestamp ?? 0) - (b.points[0]?.timestamp ?? 0))[0] ?? null
    }
    if (exit) claimed.add(exit)
    claimed.add(entry)
    const key = typeof sid === 'string' ? `setup:${sid}` : `setup:${entry.points[0]?.timestamp ?? 0}:${entry.points[0]?.value ?? 0}`
    setups.push({ key, entry, exit })
  }

  const setupTexts = setups.map((s, i) => {
    const text = clip(setupBlock(i + 1, s.entry, s.exit, ctx), ITEM_CAP)
    items[s.key] = text
    return text
  })

  const legKey = (d: SavedDrawing) => {
    const cid = ed(d).ctxId
    return typeof cid === 'string' ? `leg:${cid}` : `leg:${d.name}:${d.points[0]?.timestamp ?? 0}:${d.points[0]?.value ?? 0}`
  }
  const legTexts = [...exits.filter((x) => !claimed.has(x)), ...otherLegs].map((d) => {
    const text = clip(legLine(d, ctx), ITEM_CAP)
    items[legKey(d)] = text
    return text
  })

  // Per-base ordinal keys: the first instance keeps `ind:<name>` (legacy
  // sentItems keys stay valid), duplicates get `ind:<name>#2`, `#3`, … in
  // array order — the same order restore mints engine names in.
  const indOrdinals: Record<string, number> = {}
  const indicatorTexts = state.indicators.map((i) => {
    const k = (indOrdinals[i.name] = (indOrdinals[i.name] ?? 0) + 1)
    const key = k === 1 ? `ind:${i.name}` : `ind:${i.name}#${k}`
    const pane = indicatorByName(i.name)?.pane === 'main' ? 'overlaid on price' : 'own pane'
    const params = i.settings.params.length > 0 ? ` (params: ${i.settings.params.join(', ')})` : ''
    const text = `${i.name}${params}, ${pane}`
    items[key] = text
    return text
  })
  if (state.volume.present) {
    items['ind:Volume'] = 'Volume, overlaid on price'
    indicatorTexts.push('Volume, overlaid on price')
  }

  const geo = drawable.filter((d) => !LEG_NAMES.includes(d.name))
  const droppedDrawings = Math.max(0, geo.length - MAX_DRAW_ITEMS)
  const drawingTexts = geo.slice(0, MAX_DRAW_ITEMS).map((d) => {
    const cid = ed(d).ctxId
    const key = typeof cid === 'string' ? `draw:${cid}` : `draw:${d.name}:${d.points[0]?.timestamp ?? 0}:${d.points[0]?.value ?? 0}`
    const text = clip(drawingLine(d, ctx), ITEM_CAP)
    items[key] = text
    return text
  })

  const noteTexts = state.notes
    .filter((n) => n.text.trim())
    .map((n) => {
      const text = clip(`"${clip(n.text.trim(), NOTE_CAP)}" @ ${ctx.price(n.pos.value)} (${fmtTs(n.pos.timestamp)} UTC)`, ITEM_CAP)
      items[`note:${n.id}`] = text
      return text
    })

  return { items, setupTexts, legTexts, indicatorTexts, drawingTexts, noteTexts, droppedDrawings }
}

function section(title: string, lines: string[]): string[] {
  if (lines.length === 0) return []
  return ['', `${title}:`, ...lines.map((l) => (l.includes('\n') ? l : `- ${l}`))]
}

export function buildChartContext(state: ChartContextState, opts: BuildChartContextOpts = {}): ChartContext {
  const precision = opts.precision ?? 5
  const ctx: Ctx = {
    price: (v) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(precision) : '?'),
  }
  const now = opts.now ?? new Date()
  const tf = TIMEFRAME_LABELS[state.timeframe as keyof typeof TIMEFRAME_LABELS] ?? state.timeframe
  const chartTypeLabel = CHART_TYPES.find((t) => t.id === state.chartType)?.label.toLowerCase() ?? state.chartType
  const label = `${state.symbol} · ${tf} chart`
  const windowLine = opts.window ? `Visible window: ${fmtTs(opts.window.fromTs)} → ${fmtTs(opts.window.toTs)} UTC` : null

  const { items, setupTexts, legTexts, indicatorTexts, drawingTexts, noteTexts, droppedDrawings } = collect(state, ctx)

  const prev = opts.prevSentItems ?? null
  let body: string[]

  if (!prev) {
    body = [
      `[CHART] ${state.symbol} · ${tf} ${chartTypeLabel} — shared ${fmtTs(now.getTime())} UTC`,
      ...(windowLine ? [windowLine] : []),
      ...(setupTexts.length > 0 ? ['', 'Trade setups:', ...setupTexts] : []),
      ...section('Unpaired trade markers', legTexts),
      ...section('Indicators', indicatorTexts),
      ...section('Drawings', [...drawingTexts, ...(droppedDrawings > 0 ? [`…and ${droppedDrawings} more drawings`] : [])]),
      ...section('Notes', noteTexts),
    ]
  } else {
    const added: string[] = []
    const changed: string[] = []
    const removed: string[] = []
    let tradeTouched = false
    for (const [key, text] of Object.entries(items)) {
      const isTrade = key.startsWith('setup:') || key.startsWith('leg:')
      if (!(key in prev)) {
        added.push(text)
        if (isTrade) tradeTouched = true
      } else if (prev[key] !== text) {
        changed.push(text)
        if (isTrade) tradeTouched = true
      }
    }
    for (const [key, text] of Object.entries(prev)) {
      if (!(key in items)) {
        removed.push(text.split('\n')[0])
        if (key.startsWith('setup:') || key.startsWith('leg:')) tradeTouched = true
      }
    }

    const header = `[CHART UPDATE] ${state.symbol} · ${tf} ${chartTypeLabel} — changes since the last shared chart (${fmtTs(now.getTime())} UTC)`
    if (added.length === 0 && changed.length === 0 && removed.length === 0) {
      body = [header.replace('— changes since', '— no drawing/indicator changes since'), ...(windowLine ? [windowLine] : [])]
    } else {
      body = [
        header,
        ...section('Added', added),
        ...section('Changed', changed),
        ...section('Removed', removed),
        // Trade setups are the payload the AI reasons about — restate them
        // whole whenever any marker moved, so the model never has to patch
        // a setup together from fragments.
        ...(tradeTouched && setupTexts.length > 0 ? ['', 'Current trade setups (in full):', ...setupTexts] : []),
        ...(windowLine ? ['', windowLine] : []),
      ]
    }
  }

  let text = body.join('\n')
  if (text.length > TEXT_CAP) text = `${text.slice(0, TEXT_CAP - 12)}\n[truncated]`

  return { label, text, items }
}
